import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import apiClient from '../services/apiClient';
import { ENDPOINTS } from '../constants/endpoints';
import {
  ROLES,
  ROLE_LABELS,
  PARTNER_ACCESSIBLE_ROUTES,
  PLATFORM_ONLY_ROUTES,
} from '../constants/permissions';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [adminContext, setAdminContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch admin context from API
  const fetchAdminContext = useCallback(async () => {
    if (!isAuthenticated()) {
      setAdminContext(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(ENDPOINTS.PARTNER_ADMIN.CONTEXT);
      setAdminContext(response.data);
    } catch (err) {
      console.error('Failed to fetch admin context:', err);
      setError(err.message);
      setAdminContext({
        isPlatformAdmin: user?.isAdmin === true,
        partnerMemberships: [],
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.isAdmin]);

  useEffect(() => {
    fetchAdminContext();
  }, [fetchAdminContext]);

  // Check if user is platform admin
  const isPlatformAdmin = adminContext?.isPlatformAdmin === true || user?.isAdmin === true;

  // Partner admins always have the ADMIN role — return it if they have any membership
  const getHighestPartnerRole = () => {
    const memberships = adminContext?.partnerMemberships || [];
    if (memberships.length === 0) return null;
    return ROLES.ADMIN;
  };

  // Get role label for display
  const getRoleLabel = () => {
    if (isPlatformAdmin) {
      return ROLE_LABELS.PLATFORM_ADMIN;
    }
    const memberships = adminContext?.partnerMemberships || [];
    if (memberships.length > 0) {
      return ROLE_LABELS.PARTNER_ADMIN;
    }
    return 'User';
  };

  // Check if user can access a specific route
  const canAccessRoute = (path) => {
    if (isPlatformAdmin) {
      return true;
    }

    const normalizedPath = path.split('?')[0].replace(/\/$/, '') || '/';

    for (const route of PLATFORM_ONLY_ROUTES) {
      if (normalizedPath === route || normalizedPath.startsWith(route + '/')) {
        return false;
      }
    }

    for (const route of PARTNER_ACCESSIBLE_ROUTES) {
      if (normalizedPath === route || normalizedPath.startsWith(route + '/')) {
        return true;
      }
    }

    return normalizedPath === '/' || normalizedPath === '';
  };

  // Get default route for user
  const getDefaultRoute = () => {
    return '/admin-dashboard';
  };

  // Check if user has a specific permission (across any membership)
  const hasPermission = (permissionName) => {
    if (isPlatformAdmin) {
      return true;
    }

    const memberships = adminContext?.partnerMemberships || [];
    return memberships.some(m => {
      const permissions = m.permissions || 0;
      return (permissions & permissionName) !== 0;
    });
  };

  // Check if user has permission for a specific partner
  const hasPermissionForPartner = (partnerId, permissionName) => {
    if (isPlatformAdmin) {
      return true;
    }

    const membership = (adminContext?.partnerMemberships || []).find(
      m => String(m.partnerId) === String(partnerId)
    );
    if (!membership) return false;

    const permissions = membership.permissions || 0;
    return (permissions & permissionName) !== 0;
  };

  // No viewer role exists — partner admins always have manage access
  const isViewer = () => false;

  const canManage = () => {
    if (isPlatformAdmin) return true;
    return (adminContext?.partnerMemberships || []).length > 0;
  };

  // Get accessible partner IDs
  const getAccessiblePartnerIds = () => {
    if (isPlatformAdmin) {
      return null; // null means all partners
    }

    return (adminContext?.partnerMemberships || []).map(m => m.partnerId);
  };

  const value = {
    adminContext,
    loading,
    error,
    isPlatformAdmin,
    getRoleLabel,
    canAccessRoute,
    getDefaultRoute,
    hasPermission,
    hasPermissionForPartner,
    isViewer,
    canManage,
    getAccessiblePartnerIds,
    getHighestPartnerRole,
    refetchContext: fetchAdminContext,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
